import React from 'react';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

// Muestra un valor en su moneda original + conversion opcional a CLP.
// Fase 3 del roadmap (multi-moneda). Transparencia total: al hover muestra
// tasa y fecha del snapshot.
//
// Prop `theme`: 'light' (default) o 'dark'. En dark, los textos usan
// colores claros para legibilidad sobre fondo oscuro.

const FORMATO = {
  CLP: { symbol: '$', locale: 'es-CL', decimals: 0 },
  UF: { symbol: 'UF', locale: 'es-CL', decimals: 2 },
  USD: { symbol: 'US$', locale: 'en-US', decimals: 2 },
};

const formatear = (valor, moneda) => {
  const meta = FORMATO[moneda] || FORMATO.CLP;
  const numero = Number(valor ?? 0).toLocaleString(meta.locale, {
    minimumFractionDigits: meta.decimals,
    maximumFractionDigits: meta.decimals,
  });
  return `${meta.symbol} ${numero}`;
};

const PALETTES = {
  light: {
    amount: '#0f172a',
    converted: '#475569',
    arrow: '#94a3b8',
    tag: '#64748b',
    tagBg: '#eef1f4',
    tagBorder: '#e2e6eb',
    info: '#94a3b8',
  },
  dark: {
    amount: '#f8fafc',
    converted: '#cbd5e1',
    arrow: '#cbd5e1',
    tag: '#e2e8f0',
    tagBg: 'rgba(255,255,255,0.12)',
    tagBorder: 'rgba(255,255,255,0.2)',
    info: '#cbd5e1',
  },
};

function MonedaBadge({
  amount,
  moneda = 'CLP',
  convertedTo,
  convertedAmount,
  rate,
  rateDate,
  size = 'md',
  inline = true,
  theme = 'light',
}) {
  const Wrapper = inline ? 'span' : 'div';
  const fontSize = size === 'sm' ? 12 : size === 'lg' ? 16 : 13;
  const monedaFontSize = size === 'sm' ? 10 : size === 'lg' ? 12 : 11;
  const palette = PALETTES[theme] || PALETTES.light;

  const hasConversion =
    convertedTo && convertedTo !== moneda && typeof convertedAmount === 'number';

  const conversionTooltip =
    hasConversion && rate ? (
      <div style={{ fontSize: 11 }}>
        <div>
          1 {moneda} = {Number(rate).toLocaleString('es-CL', { maximumFractionDigits: 4 })} CLP
        </div>
        {rateDate ? <div style={{ opacity: 0.7, marginTop: 2 }}>snapshot: {rateDate}</div> : null}
      </div>
    ) : null;

  return (
    <Wrapper
      style={{
        display: inline ? 'inline-flex' : 'flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: "'IBM Plex Mono', monospace",
      }}
    >
      <span
        style={{
          fontSize: monedaFontSize,
          fontWeight: 600,
          letterSpacing: 0.6,
          color: palette.tag,
          padding: '1px 5px',
          borderRadius: 3,
          background: palette.tagBg,
          border: `1px solid ${palette.tagBorder}`,
        }}
      >
        {moneda}
      </span>
      <span style={{ fontSize, fontWeight: 500, color: palette.amount }}>
        {formatear(amount, moneda)}
      </span>
      {hasConversion ? (
        <>
          <span style={{ color: palette.arrow, fontSize }}>↔</span>
          <span
            style={{
              fontSize: fontSize - 1,
              color: palette.converted,
            }}
          >
            {formatear(convertedAmount, convertedTo)}
          </span>
          <Tooltip title={conversionTooltip}>
            <InfoCircleOutlined style={{ fontSize: 11, color: palette.info, cursor: 'help' }} />
          </Tooltip>
        </>
      ) : null}
    </Wrapper>
  );
}

export default MonedaBadge;
